import { Modal, Input, Empty, Table, Tag, Button, message } from 'antd';
import { GiftOutlined, PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { usePromotionList } from '@/hooks/usePromotions';
import { PROMOTION_TYPE_LABEL, type PromotionType } from '@/mocks/promotions';
import { ROUTES } from '@/constants/routes';
import { formatVND, formatDate } from '@/utils/format';
import './VoucherModal.css';

export interface VoucherItem {
  code: string;
  label: string;
  description?: string;
  discountAmount?: number;
  discountPercent?: number;
  minOrderValue?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onConfirm: (vouchers: VoucherItem[]) => void;
  selectedCodes?: string[];
  orderTotal: number;
}

function toVoucherItem(p: any): VoucherItem {
  const action = p.actions?.[0];
  return {
    code: p.code,
    label: p.name,
    description: PROMOTION_TYPE_LABEL[p.type as PromotionType] || p.type,
    discountPercent: action?.discount_percent,
    discountAmount: action?.discount_amount,
    minOrderValue: p.conditions?.min_order_amount,
  };
}

function discountLabel(p: any): string {
  const action = p.actions?.[0];
  if (action?.discount_percent) return `Giảm ${action.discount_percent}%`;
  if (action?.discount_amount) return `Giảm ${formatVND(action.discount_amount)}`;
  return PROMOTION_TYPE_LABEL[p.type as PromotionType] || '—';
}

function isEligible(p: any, orderTotal: number): boolean {
  const minOrder = p.conditions?.min_order_amount;
  if (minOrder && orderTotal < minOrder) return false;
  return true;
}

export function VoucherModal({ open, onClose, onConfirm, selectedCodes, orderTotal }: Props) {
  const { data: resData, isFetching } = usePromotionList({ limit: 100, status: 'active' });
  const [query, setQuery] = useState('');
  const [checked, setChecked] = useState<string[]>([]);

  // Seed lựa chọn từ parent + reset ô tìm mỗi lần mở.
  useEffect(() => {
    if (open) {
      setChecked(selectedCodes ?? []);
      setQuery('');
    }
  }, [open, selectedCodes]);

  // Chỉ CTKM đang chạy mới xuất hiện trong luồng bán hàng.
  const activePromotions = useMemo(() => {
    const list = resData?.data ?? [];
    return list.filter((p: any) => p.status === 'active');
  }, [resData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return activePromotions;
    return activePromotions.filter(
      (p: any) => p.code.toLowerCase().includes(q) || p.name.toLowerCase().includes(q),
    );
  }, [activePromotions, query]);

  function applyCode() {
    const code = query.trim();
    if (!code) return;
    const match = activePromotions.find((p: any) => p.code.toLowerCase() === code.toLowerCase());
    if (!match) {
      message.error('Mã giảm giá không hợp lệ');
      return;
    }
    if (!isEligible(match, orderTotal)) {
      message.warning(`Chưa đạt đơn tối thiểu ${formatVND(match.conditions?.min_order_amount ?? 0)}`);
      return;
    }
    setChecked((prev) => (prev.includes(match.code) ? prev : [...prev, match.code]));
    message.success(`Đã áp dụng ${match.code}`);
  }

  function handleConfirm() {
    const selected = activePromotions.filter((p: any) => checked.includes(p.code)).map(toVoucherItem);
    onConfirm(selected);
  }

  const columns: ColumnsType<any> = [
    {
      title: 'Tên CTKM',
      dataIndex: 'code',
      key: 'code',
      render: (_: string, p: any) => (
        <div>
          <div style={{ fontWeight: 600 }}>{p.code}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{p.name}</div>
        </div>
      ),
    },
    { title: 'Khách hàng', key: 'customer', render: () => 'Tất cả KH' },
    { title: 'Ngày bắt đầu', key: 'startAt', render: (_: unknown, p: any) => formatDate(p.schedule?.start_date) },
    { title: 'Ngày kết thúc', key: 'endAt', render: (_: unknown, p: any) => formatDate(p.schedule?.end_date) },
    { title: 'Loại khuyến mãi', key: 'type', render: (_: unknown, p: any) => discountLabel(p) },
    {
      title: 'Đơn tối thiểu',
      key: 'minOrder',
      align: 'right',
      render: (_: unknown, p: any) => {
        const minOrder = p.conditions?.min_order_amount;
        return minOrder ? formatVND(minOrder) : '—';
      },
    },
    {
      title: 'Trạng thái',
      key: 'status',
      render: (_: unknown, p: any) =>
        isEligible(p, orderTotal) ? (
          <Tag color="green">Có thể áp dụng</Tag>
        ) : (
          <Tag color="orange">Chưa đạt đơn tối thiểu</Tag>
        ),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={760}
      destroyOnClose
      title={
        <span>
          <GiftOutlined style={{ color: '#a4193d', marginRight: 8 }} />
          KHUYẾN MÃI &amp; VOUCHER
        </span>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            icon={<PlusOutlined />}
            onClick={() => window.open(ROUTES.promotions.new, '_blank', 'noopener,noreferrer')}
          >
            Thêm khuyến mãi
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={onClose}>Đóng</Button>
            <Button type="primary" onClick={handleConfirm}>
              Đồng ý
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <Input
          placeholder="Nhập mã giảm giá"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPressEnter={applyCode}
          allowClear
        />
        <Button type="primary" onClick={applyCode}>
          Áp dụng
        </Button>
      </div>

      <Table<any>
        className="voucher-ctkm-table"
        rowKey="code"
        size="small"
        columns={columns}
        dataSource={filtered}
        loading={isFetching}
        pagination={false}
        scroll={{ x: 'max-content', y: 360 }}
        locale={{ emptyText: <Empty description="Chưa có chương trình khuyến mãi nào" /> }}
        rowSelection={{
          selectedRowKeys: checked,
          preserveSelectedRowKeys: true,
          onChange: (keys) => setChecked(keys as string[]),
          getCheckboxProps: (p: any) => ({ disabled: !isEligible(p, orderTotal) }),
        }}
      />
    </Modal>
  );
}
